import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { notificationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Shield, LayoutDashboard, Users, Link2, CreditCard, UserCircle,
  LogOut, Menu, ChevronRight, ExternalLink, Settings, Key, MessageSquare, Receipt, Bell, FileText, UsersRound
} from 'lucide-react';

const ROLE_COLORS = {
  root: 'bg-red-500/15 text-red-500 border-red-500/20 border',
  administrator: 'bg-blue-500/15 text-blue-500 border-blue-500/20 border',
  integrator: 'bg-purple-500/15 text-purple-500 border-purple-500/20 border',
  user: 'bg-green-500/15 text-green-500 border-green-500/20 border',
};

const ROLE_LABELS = {
  root: 'Root',
  administrator: 'Admin',
  integrator: 'Integratore',
  user: 'Utente',
};

function getNavItems(role) {
  const items = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['root', 'administrator', 'integrator', 'user'] },
    { to: '/users', icon: Users, label: 'Gestione Utenti', roles: ['root', 'administrator'] },
    { to: '/links', icon: Link2, label: 'Gestione OBC', roles: ['root', 'administrator', 'integrator'] },
    { to: '/my-links', icon: ExternalLink, label: 'I Miei OBC', roles: ['user', 'integrator'] },
    { to: '/team', icon: UsersRound, label: 'Il Mio Team', roles: ['integrator'] },
    { to: '/admin/settings', icon: Settings, label: 'Config. Sistema', roles: ['root'] },
    { to: '/admin/subscription-config', icon: CreditCard, label: 'Piani Abbonamento', roles: ['root'] },
    { to: '/admin/tokens', icon: Key, label: 'Token OBC', roles: ['root', 'administrator'] },
    { to: '/admin/audit', icon: FileText, label: 'Audit Log', roles: ['root', 'administrator'] },
    { to: '/tickets', icon: MessageSquare, label: 'Ticket Supporto', roles: ['root', 'administrator', 'integrator', 'user'] },
    { to: '/subscription', icon: CreditCard, label: 'Abbonamento', roles: ['user', 'integrator'] },
    { to: '/billing', icon: Receipt, label: 'Fatturazione', roles: ['user', 'integrator'] },
    { to: '/profile', icon: UserCircle, label: 'Profilo', roles: ['root', 'administrator', 'integrator', 'user'] },
  ];
  return items.filter(item => item.roles.includes(role));
}

function NavContent({ user, navItems, currentPath, onLogout, onClose }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
            <img src="/obc-logo.png" alt="OBC Cloud" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <h2 className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>OBC Cloud</h2>
            <span className={`text-xs px-1.5 py-0.5 rounded ${ROLE_COLORS[user?.role] || ''}`}>
              {ROLE_LABELS[user?.role] || user?.role}
            </span>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label }) => {
          const isActive = currentPath === to;
          return (
            <Link
              key={to}
              to={to}
              onClick={onClose}
              data-testid={`nav-${to.replace('/', '')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 cursor-pointer ${
                isActive
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {isActive && <ChevronRight className="w-3 h-3" />}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <Button
          variant="ghost"
          data-testid="logout-btn"
          className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          onClick={onLogout}
        >
          <LogOut className="w-4 h-4 mr-3" />
          Esci
        </Button>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const navItems = getNavItems(user?.role);
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    loadNotifications();
    // Poll every 10 seconds for real-time updates
    const interval = setInterval(loadNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await notificationsAPI.getAll();
      setNotifications(res.data);
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 fixed left-0 top-0 h-full z-30">
        <NavContent
          user={user}
          navItems={navItems}
          currentPath={location.pathname}
          onLogout={handleLogout}
        />
      </aside>

      {/* Desktop Header with Notifications */}
      <div className="hidden md:flex fixed top-0 left-64 right-0 z-30 h-14 bg-background/80 backdrop-blur-md border-b border-border items-center px-6 justify-end">
        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center font-semibold" data-testid="unread-count">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0 bg-popover border-border" align="end">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <h4 className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>Notifiche non lette</h4>
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleMarkAllRead}>
                  Segna tutte lette
                </Button>
              )}
            </div>
            <ScrollArea className="max-h-80">
              {notifications.filter(n => !n.read).length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Nessuna notifica non letta
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications.filter(n => !n.read).map((notif) => (
                    <div
                      key={notif.notif_id}
                      data-testid={`notif-${notif.notif_id}`}
                      className="p-3 text-sm hover:bg-accent/50 transition-colors bg-primary/5"
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 bg-primary" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs mb-0.5">{notif.title}</p>
                          <p className="text-xs text-muted-foreground">{notif.message}</p>
                          <p className="text-[10px] text-muted-foreground/60 mt-1">
                            {new Date(notif.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            <div className="p-2 border-t border-border">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => { setNotifOpen(false); window.location.href = '/notifications'; }}
              >
                Vedi tutte le notifiche
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-background/80 backdrop-blur-md border-b border-border flex items-center px-4 justify-between">
        <div className="flex items-center gap-2">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-background border-r border-border">
              <NavContent
                user={user}
                navItems={navItems}
                currentPath={location.pathname}
                onLogout={handleLogout}
                onClose={() => setSheetOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <img src="/obc-logo.png" alt="OBC Cloud" className="w-6 h-6 object-contain" />
          <span className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>OBC Cloud</span>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="mobile-notifications-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] rounded-full flex items-center justify-center font-semibold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0 bg-popover border-border" align="end">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <h4 className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>Notifiche</h4>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleMarkAllRead}>
                    Tutte lette
                  </Button>
                )}
              </div>
              <ScrollArea className="max-h-60">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-xs text-muted-foreground">
                    Nessuna notifica
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {notifications.slice(0, 5).map((notif) => (
                      <div
                        key={notif.notif_id}
                        className={`p-2.5 text-xs hover:bg-accent/50 ${!notif.read ? 'bg-primary/5' : ''}`}
                      >
                        <p className="font-medium mb-0.5">{notif.title}</p>
                        <p className="text-muted-foreground line-clamp-2">{notif.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          <Badge className={ROLE_COLORS[user?.role]}>
            {ROLE_LABELS[user?.role]}
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 mt-14 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}

export { ROLE_COLORS, ROLE_LABELS };
